import React, { useCallback, useEffect, useRef, useState } from 'react';
import { chatService, chatWebSocket } from '../../services/chatService';
import { ChatThread, ChatMessage, ChatAttachment, CNSOfficer } from '../../types/chat';
import MessageComposer from './MessageComposer';

interface ThreadDetailProps {
  thread: ChatThread;
  onThreadUpdate: () => void;
}

const ThreadDetail: React.FC<ThreadDetailProps> = ({ thread, onThreadUpdate }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [attachments, setAttachments] = useState<Record<string, ChatAttachment[]>>({});
  const [loading, setLoading] = useState(true);
  const [officers, setOfficers] = useState<CNSOfficer[]>([]);
  const [showAssignMenu, setShowAssignMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const onThreadUpdateRef = useRef(onThreadUpdate);
  onThreadUpdateRef.current = onThreadUpdate;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      const msgs = await chatService.getMessages(thread.id);
      setMessages(msgs);

      const attMap: Record<string, ChatAttachment[]> = {};
      for (const m of msgs) {
        if (m.has_attachments) {
          try {
            const atts = await chatService.getAttachments(m.id);
            if (atts && atts.length > 0) attMap[m.id] = atts;
          } catch { /* ignore */ }
        }
      }
      setAttachments(attMap);
      scrollToBottom();
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  }, [thread.id]);

  const loadOfficers = async () => {
    try {
      const data = await chatService.getAvailableOfficers();
      setOfficers(data);
    } catch (error) {
      console.error('Error loading officers:', error);
    }
  };

  useEffect(() => {
    let active = true;
    void loadMessages().then(() => {
      if (active) onThreadUpdateRef.current();
    });
    loadOfficers();
    return () => {
      active = false;
    };
  }, [loadMessages]);

  useEffect(() => {
    chatWebSocket.connect();
    const unsubscribe = chatWebSocket.onMessage((message) => {
      if (message.thread_id === thread.id) {
        loadMessages();
        onThreadUpdate();
      }
    });
    return () => {
      unsubscribe();
    };
  }, [loadMessages, onThreadUpdate, thread.id]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadMessages();
    }, 8000);
    return () => window.clearInterval(timer);
  }, [loadMessages]);

  const handleSendMessage = async (content: string, files: File[]) => {
    try {
      const message = await chatService.sendMessage(thread.id, content || 'Fichier joint');
      for (const file of files) {
        await chatService.uploadAttachment(message.id, file);
      }
      await loadMessages();
      onThreadUpdate();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Echec de l’envoi. Veuillez réessayer.');
    }
  };

  const handleStatusChange = async (status: string) => {
    try {
      await chatService.updateThreadStatus(thread.id, status);
      onThreadUpdate();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Impossible de mettre à jour le statut. Veuillez réessayer.');
    }
  };

  const handleAssign = async (officerId: string) => {
    try {
      await chatService.assignThread(thread.id, officerId);
      setShowAssignMenu(false);
      onThreadUpdate();
    } catch (error) {
      console.error('Error assigning thread:', error);
      alert('Impossible d’assigner la conversation. Veuillez réessayer.');
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await chatService.exportConversation(thread.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversation-${thread.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting conversation:', error);
      alert('Impossible d’exporter la conversation. Veuillez réessayer.');
    } finally {
      setExporting(false);
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="bg-gray-50 border-b px-6 py-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {thread.hotel_name || 'Hôtel non renseigné'}
            </h2>
            <p className="text-sm text-gray-600">
              Client: {thread.guest_name || 'Non renseigné'} ({thread.guest_document || 'N/A'})
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Conversation: {thread.id ? thread.id.slice(0, 8) : '—'}
              {thread.checkin_id ? ` · Dossier ${thread.checkin_id.slice(0, 8)}` : ''}
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 disabled:opacity-50 transition-colors"
              title="Exporter la conversation"
            >
              {exporting ? 'Export...' : 'Exporter'}
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-4">
          <div className="relative">
            <button
              onClick={() => setShowAssignMenu(!showAssignMenu)}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
            >
              {thread.assigned_to_name ? `Assigné à ${thread.assigned_to_name}` : 'Assigner'}
            </button>
            
            {showAssignMenu && (
              <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-10 w-48">
                {officers.map((officer) => (
                  <button
                    key={officer.id}
                    onClick={() => handleAssign(officer.id)}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                  >
                    {officer.name}
                    {!officer.is_available && <span className="text-gray-500 ml-2">(occupé)</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <select
            value={thread.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="px-3 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="open">Ouvert</option>
            <option value="in_progress">En cours</option>
            <option value="resolved">Résolu</option>
            <option value="closed">Fermé</option>
          </select>

          {thread.priority === 'urgent' && (
            <span className="bg-red-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
              URGENT
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-sm font-medium text-gray-700">Aucun message dans cette conversation</p>
            <p className="mt-1 text-xs text-gray-500">La réponse à l’hôtel apparaîtra ici en temps réel.</p>
          </div>
        ) : (
          messages.map((message, index) => {
            const isAgency = ['cns', 'ont', 'anr', 'admin', 'support'].includes(message.sender_type);
            const showDate = index === 0 || formatDate(messages[index - 1].created_at) !== formatDate(message.created_at);
            const agencyLabel =
              message.sender_type === 'ont' ? 'ONT'
                : message.sender_type === 'anr' ? 'ANR'
                : message.sender_type === 'admin' || message.sender_type === 'support' ? 'Admin'
                : message.sender_type === 'cns' ? 'CNS (vous)'
                : 'Agence';

            return (
              <div key={message.id}>
                {showDate && (
                  <div className="flex justify-center my-4">
                    <span className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
                      {formatDate(message.created_at)}
                    </span>
                  </div>
                )}
                <div className={`flex ${isAgency ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] ${isAgency ? 'bg-blue-600 text-white' : 'bg-white text-gray-800'} rounded-lg px-4 py-2 shadow`}>
                    <div className="text-xs font-semibold mb-1 opacity-75">
                      {message.sender_name || (isAgency ? agencyLabel : 'Hôtel')}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    {(attachments[message.id] || []).map(att => (
                      <AttachmentItem key={att.id} attachment={att} messageId={message.id} />
                    ))}
                    {message.has_attachments && !(attachments[message.id]?.length) && (
                      <div className="mt-2 text-xs opacity-75">
                        Fichier joint
                      </div>
                    )}
                    <div className={`text-xs mt-1 ${isAgency ? 'text-blue-100' : 'text-gray-500'}`}>
                      {formatTime(message.created_at)}
                      {message.read_at && ' • Lu'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Composer */}
      {thread.status !== 'closed' && (
        <MessageComposer onSend={handleSendMessage} />
      )}
      
      {thread.status === 'closed' && (
        <div className="px-6 py-4 bg-gray-100 text-center text-gray-600">
          Cette conversation est fermée
        </div>
      )}
    </div>
  );
};

// Inline attachment display with download
function AttachmentItem({ attachment, messageId }: { attachment: ChatAttachment; messageId: string }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await chatService.downloadAttachment(messageId, attachment.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const sizeLabel = attachment.file_size < 1024 * 1024
    ? `${(attachment.file_size / 1024).toFixed(0)} KB`
    : `${(attachment.file_size / (1024 * 1024)).toFixed(1)} MB`;

  return (
    <div className="mt-2 p-2 bg-black bg-opacity-10 rounded flex items-center space-x-2 text-xs cursor-pointer hover:bg-opacity-20 transition-colors" onClick={handleDownload}>
      <span>{/\.(jpe?g|png)$/i.test(attachment.file_name) ? '🖼' : '📎'}</span>
      <span className="truncate max-w-[140px]">{attachment.file_name}</span>
      <span className="opacity-75">({sizeLabel})</span>
      {downloading ? (
        <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin ml-auto" />
      ) : (
        <span className="ml-auto">⬇</span>
      )}
    </div>
  );
}

export default ThreadDetail;
